"use client";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { cloneElement, Children, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, } from "react";
import { useScroll } from "@travel/shared/hooks";
import classNames from "classnames";
import styles from "./box-list.module.css";
export var BoxList = forwardRef(function BoxList(_a, ref) {
    var className = _a.className, boxClassName = _a.boxClassName, children = _a.children, _b = _a.direction, direction = _b === void 0 ? "vertical" : _b, _c = _a.windowScroller, windowScroller = _c === void 0 ? true : _c, _d = _a.scrollOffset, scrollOffset = _d === void 0 ? 0 : _d, _e = _a.scrolls, scrolls = _e === void 0 ? true : _e, _f = _a.waitForTransition, waitForTransition = _f === void 0 ? false : _f, _g = _a.overrideTargets, overrideTargets = _g === void 0 ? [] : _g, renderBox = _a.renderBox, onChange = _a.onChange, override = _a.override, onScrollStopHandler = _a.onScrollStop, onMostVisibleChange = _a.onMostVisibleChange;
    var scrollerRef = useRef(null);
    var boxes = useRef([]);
    var stateRef = useRef({
        all: new Map(),
        visible: new Map(),
    });
    var isScrollingRef = useRef(false);
    var isScrollingToTargetRef = useRef(false);
    var shieldRef = useRef(null);
    var isTransitioningRef = useRef(false);
    var transitionTimeoutRef = useRef();
    var targetIndex = useRef(-1);
    var onScrollStart = useCallback(function () {
        isScrollingRef.current = true;
    }, []);
    var onScrollStop = useCallback(function (_a) {
        var isScrollingUp = _a.isScrollingUp, isScrollingDown = _a.isScrollingDown, isScrollingRight = _a.isScrollingRight, isScrollingLeft = _a.isScrollingLeft;
        endTransition();
    }, [direction]);
    useScroll({
        onScrollStart: onScrollStart,
        onScrollStop: onScrollStop,
    }, scrolls, "box-scroller", scrollerRef);
    useImperativeHandle(ref, function () { return ({
        scrollToIndex: function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            if (waitForTransition &&
                (isScrollingRef.current || isTransitioningRef.current))
                return;
            scrollToIndex.apply(void 0, __spreadArray([], __read(args), false));
        },
        scrollToPrev: function () { return scrollTo("prev"); },
        scrollToNext: function () { return scrollTo("next"); },
    }); }, [direction]);
    useEffect(function () {
        var _a = stateRef.current, all = _a.all, visible = _a.visible;
        var observer = new IntersectionObserver(function (entries) {
            var e_1, _a;
            var _b;
            var canChange = false;
            entries.forEach(function (entry) {
                var element = entry.target;
                var isVisible = entry.isIntersecting;
                var visibilityPercentage = Math.round(entry.intersectionRatio * 100);
                var prevVisibility = all.get(element);
                var currentVisibility = __assign(__assign({}, prevVisibility), { isVisible: isVisible, percentage: visibilityPercentage });
                if (!prevVisibility || currentVisibility !== prevVisibility) {
                    all.set(element, currentVisibility);
                    if (isVisible) {
                        visible.set(element, currentVisibility);
                    }
                    else {
                        visible.delete(element);
                    }
                    canChange = true;
                }
            });
            if (canChange) {
                var mostVisible = getMostVisibleElement({ all: all, visible: visible });
                var minVisible = null;
                var maxVisible = null;
                try {
                    for (var _c = __values(visible.values()), _d = _c.next(); !_d.done; _d = _c.next()) {
                        var box = _d.value;
                        if (!minVisible || (minVisible && minVisible.index > box.index)) {
                            minVisible = box;
                        }
                        if (!maxVisible || (maxVisible && maxVisible.index < box.index)) {
                            maxVisible = box;
                        }
                    }
                }
                catch (e_1_1) { e_1 = { error: e_1_1 }; }
                finally {
                    try {
                        if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
                    }
                    finally { if (e_1) throw e_1.error; }
                }
                if (((_b = stateRef.current.mostVisible) === null || _b === void 0 ? void 0 : _b.index) !== (mostVisible === null || mostVisible === void 0 ? void 0 : mostVisible.index)) {
                    onMostVisibleChange === null || onMostVisibleChange === void 0 ? void 0 : onMostVisibleChange(mostVisible);
                }
                stateRef.current.mostVisible = mostVisible;
                stateRef.current.minVisible = minVisible;
                stateRef.current.maxVisible = maxVisible;
                onChange === null || onChange === void 0 ? void 0 : onChange(stateRef.current);
            }
        }, { threshold: [0.1, 0.25, 0.5, 0.75, 0.99] });
        boxes.current.forEach(function (box, index) {
            if (!box)
                return;
            all.set(box, {
                index: index,
                element: box,
                isVisible: false,
                percentage: 0,
            });
            observer.observe(box);
        });
        return function () { return observer.disconnect(); };
    }, [onChange, direction, Children.count(children)]);
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: classNames(styles["container"], className, styles[direction]), ref: scrollerRef, children: Children.map(children, function (child, index) {
                    return override ? (cloneElement(child, {
                        className: boxClassName,
                        children: child,
                        key: index,
                        ref: function (el) {
                            boxes.current[index] = el;
                        },
                    })) : (
                    /*renderBox?.(
                      {
                        className: boxClassName,
                        children: child,
                        key: index,
                        ref: (el: any) => {
                          boxes.current[index] = el;
                        },
                      },
                      index
                    ) || */ _jsx("div", { className: boxClassName, ref: function (el) {
                            boxes.current[index] = el;
                        }, children: child }, index));
                }) }), _jsx("div", { className: styles["shield"], ref: shieldRef })] }));
    function scrollToIndex(index, instant, anchor) {
        var e_2, _a;
        if (instant === void 0) { instant = false; }
        if (anchor === void 0) { anchor = "start"; }
        if (targetIndex.current === index)
            return;
        try {
            //if (stateRef.current.mostVisible?.index === index) return;
            for (var _b = __values(stateRef.current.all), _c = _b.next(); !_c.done; _c = _b.next()) {
                var _d = __read(_c.value, 2), element = _d[0], box = _d[1];
                if (box.index === index) {
                    if (box.percentage < 95) {
                        targetIndex.current = index;
                        scrollToBox(box, instant, anchor);
                    }
                    break;
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_2) throw e_2.error; }
        }
    }
    function scrollToBox(_a, instant, anchor) {
        var _b, _c, _d, _e, _f, _g, _h, _j;
        var element = _a.element, index = _a.index;
        if (instant === void 0) { instant = false; }
        if (anchor === void 0) { anchor = "start"; }
        if (!scrollerRef.current ||
            (waitForTransition &&
                (isScrollingRef.current || isTransitioningRef.current)))
            return;
        var overrideTarget = overrideTargets[index];
        if (overrideTarget) {
            element = document.querySelector(overrideTarget);
        }
        var scroller = overrideTarget || windowScroller ? window : scrollerRef.current;
        var top = direction === "vertical"
            ? anchor === "end"
                ? element.offsetTop -
                    (((_b = scrollerRef.current) === null || _b === void 0 ? void 0 : _b.offsetHeight) - element.offsetHeight)
                : element.offsetTop -
                    scrollOffset -
                    (anchor === "center"
                        ? ((_c = scrollerRef.current) === null || _c === void 0 ? void 0 : _c.offsetHeight) / 2 - element.offsetHeight / 2
                        : 0)
            : 0;
        var left = direction === "horizontal"
            ? anchor === "end"
                ? element.offsetLeft -
                    (((_d = scrollerRef.current) === null || _d === void 0 ? void 0 : _d.offsetWidth) - element.offsetWidth)
                : element.offsetLeft -
                    scrollOffset -
                    (anchor === "center"
                        ? ((_e = scrollerRef.current) === null || _e === void 0 ? void 0 : _e.offsetWidth) / 2 - element.offsetWidth / 2
                        : 0)
            : 0;
        var minVisibleElement = (_g = (_f = stateRef.current) === null || _f === void 0 ? void 0 : _f.minVisible) === null || _g === void 0 ? void 0 : _g.element;
        var maxVisibleElement = (_j = (_h = stateRef.current) === null || _h === void 0 ? void 0 : _h.maxVisible) === null || _j === void 0 ? void 0 : _j.element;
        if (minVisibleElement && anchor === "end") {
            left = minVisibleElement.offsetWidth + minVisibleElement.offsetLeft;
        } /* else if (minVisibleElement && anchor !== "end") {
          left = minVisibleElement.offsetLeft;
        }*/
        scroller.scrollTo({
            top: top,
            left: left,
            behavior: (instant ? "instant" : "smooth"),
        });
        if (!instant) {
            startTransition();
            isScrollingToTargetRef.current = true;
        }
        else {
            isScrollingToTargetRef.current = true;
            //isTransitioningRef.current = true;
        }
    }
    function scrollTo(direction) {
        if (waitForTransition &&
            (isScrollingRef.current || isTransitioningRef.current))
            return;
        var box = getNextOrPrevElement(direction, stateRef.current);
        if (box && box.percentage < 95) {
            scrollToBox(box, false, direction === "next" ? "end" : "start");
        }
    }
    function startTransition() {
        if (shieldRef.current) {
            //shieldRef.current.style.display = "block";
        }
        clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = setTimeout(function () {
            endTransition();
        }, 1000);
        isTransitioningRef.current = true;
    }
    function endTransition() {
        //setTimeout(() => {
        if (shieldRef.current) {
            //shieldRef.current.style.display = "none";
        }
        targetIndex.current = -1;
        isScrollingRef.current = false;
        isTransitioningRef.current = false;
        //}, 50);
    }
});
function getMostVisibleElement(_a) {
    var all = _a.all, visible = _a.visible;
    var mostVisible = null;
    var mostVisibilityPercentage = 0;
    visible.forEach(function (state, element) {
        if (state.percentage > mostVisibilityPercentage) {
            mostVisible = state;
            mostVisibilityPercentage = state.percentage;
        }
    });
    return mostVisible;
}
function getNextOrPrevElement(direction, _a) {
    var e_3, _b;
    var visible = _a.visible, all = _a.all;
    var targetIndex = direction === "next" ? 0 : Infinity;
    var targetBox = null;
    visible.forEach(function (box) {
        var index = box.index;
        if (direction === "next" && index >= targetIndex) {
            targetIndex = index;
            targetBox = box;
        }
        else if (direction === "prev" && index <= targetIndex) {
            targetIndex = index;
            targetBox = box;
        }
    });
    if (targetBox) {
        if (targetBox.percentage > 90) {
            try {
                for (var _c = __values(all.values()), _d = _c.next(); !_d.done; _d = _c.next()) {
                    var box = _d.value;
                    if (direction === "next") {
                        if (box.index === targetBox.index + 1) {
                            targetBox = box;
                            break;
                        }
                    }
                    else if (direction === "prev") {
                        if (box.index === targetBox.index - 1) {
                            targetBox = box;
                            break;
                        }
                    }
                }
            }
            catch (e_3_1) { e_3 = { error: e_3_1 }; }
            finally {
                try {
                    if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
                }
                finally { if (e_3) throw e_3.error; }
            }
        }
    }
    return targetBox;
}
